import React from 'react';
import LLMChatInterface from './LLMChatInterface.js';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

// Define teacher agents with specialized prompts
const teachers = [
    { name: 'Math Teacher', prompt: 'You are a Math Teacher who explains mathematical concepts clearly and provides step-by-step problem-solving guidance.' },
    { name: 'Science Teacher', prompt: 'You are a Science Teacher specializing in physics, chemistry, and biology, offering clear explanations and real-world examples.' },
    { name: 'History Teacher', prompt: 'You are a History Teacher who provides engaging stories, timelines, and context for historical events.' },
    { name: 'English Teacher', prompt: 'You are an English Teacher expert in literature, grammar, and writing, offering analysis and writing tips.' },
    { name: 'Art Teacher', prompt: 'You are an Art Teacher knowledgeable about art history, techniques, and creative exercises.' },
    { name: 'Music Teacher', prompt: 'You are a Music Teacher specializing in theory, instrument techniques, and music appreciation.' }
];

function LLMPage({ onBackClick }) {
    // Get user from auth context
    const { user, loading } = useAuth();
    
    try {
        React.useEffect(() => {
            window.scrollTo(0, 0);
            
            // Log authentication information for debugging
            console.log('LLMPage - User authentication status:', {
                isAuthenticated: !!user,
                userId: user?.id,
                userEmail: user?.email,
                userRole: user?.role
            });
        }, [user]);

        const [activeTab, setActiveTab] = React.useState('chat');
        const [showSignupForm, setShowSignupForm] = React.useState(false);
        const [signupLoading, setSignupLoading] = React.useState(false);
        const [signupError, setSignupError] = React.useState('');
        const [selectedTeacher, setSelectedTeacher] = React.useState(null);

        // Check if user is authenticated
        const isAuthenticated = !!user;
        
        React.useEffect(() => {
            // Log authentication status when user object changes
            console.log('LLMPage - Authentication status updated:', { 
                isAuthenticated, 
                userEmail: user?.email,
                userRole: user?.role,
                userMetadata: user?.metadata 
            });
        }, [user, isAuthenticated]);

        const handleSignup = async (e) => {
            e.preventDefault();
            setSignupLoading(true);
            setSignupError('');
            
            try {
                const email = e.target.email.value;
                if (!email) {
                    throw new Error('Email is required');
                }

                // Use signupService to add email to early access signups
                if (!window.signupService || typeof window.signupService.addEarlyAccessSignup !== 'function') {
                    throw new Error('Signup service not available');
                }
                await window.signupService.addEarlyAccessSignup(email);

                // If signupService throws due to duplicate, catch below

                setShowSignupForm(false);
                alert("Thanks for signing up! We'll notify you when Sequoia Green AI launches.");
            } catch (error) {
                console.error('Signup error:', error);
                setSignupError(error.message?.includes('duplicate') || error.message?.includes('unique')
                    ? 'You are already on the waitlist!'
                    : (error.message || 'Failed to sign up. Please try again.')
                );
            } finally {
                setSignupLoading(false);
            }
        };

        return (
            <section data-name="llm-page" className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
                {/* Floating particles background */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {Array.from({ length: 30 }).map((_, i) => (
                        <div 
                            key={i} 
                            className="absolute bg-green-500/20 rounded-full blur-sm"
                            style={{
                                width: `${Math.random() * 12 + 4}px`,
                                height: `${Math.random() * 12 + 4}px`,
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animation: `floatParticle ${Math.random() * 15 + 10}s infinite ease-in-out`,
                                animationDelay: `${Math.random() * 5}s`
                            }}
                        />
                    ))}
                </div>

                {/* Simplified Navigation Bar - Just a back button */}
                <div className="container mx-auto px-4 py-6 relative z-10">
                    <div className="flex justify-between items-center mb-8">
                        <button 
                            onClick={onBackClick}
                            className="flex items-center text-green-400 hover:text-green-300 transition-colors"
                        >
                            <i className="fas fa-arrow-left mr-2"></i> Back to Home
                        </button>
                        
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-green-400 font-medium">AI Active</span>
                        </div>
                    </div>
                </div>

                {/* Header with branding and tab navigation */}
                <div className="container mx-auto px-4 py-6 relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                        <div className="mb-6 md:mb-0">
                            <h1 className="text-4xl md:text-5xl font-bold flex items-center">
                                <span className="text-gradient bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-600">Sequoia A.I.</span>
                            </h1>
                            <p className="text-gray-300 mt-2 max-w-xl">
                                Sustainable intelligence powered by renewable energy
                            </p>
                        </div>
                        <div className="bg-gray-800/50 backdrop-blur-md rounded-full p-1 border border-gray-700">
                            <div className="flex">
                                <button
                                    onClick={() => setActiveTab('chat')}
                                    className={`px-6 py-2 rounded-full transition-all ${activeTab === 'chat' ? 'bg-green-600/20 text-green-400' : 'text-gray-400 hover:text-white'}`}
                                >
                                    <i className="fas fa-comment-dots mr-2"></i> Chat
                                </button>
                                <button
                                    onClick={() => setActiveTab('green-ai')}
                                    className={`px-6 py-2 rounded-full transition-all ${activeTab === 'green-ai' ? 'bg-green-600/20 text-green-400' : 'text-gray-400 hover:text-white'}`}
                                >
                                    <i className="fas fa-leaf mr-2"></i> Green AI
                                </button>
                                <button
                                    onClick={() => setActiveTab('about')}
                                    className={`px-6 py-2 rounded-full transition-all ${activeTab === 'about' ? 'bg-green-600/20 text-green-400' : 'text-gray-400 hover:text-white'}`}
                                >
                                    <i className="fas fa-info-circle mr-2"></i> About
                                </button>
                                <button
                                    onClick={() => setActiveTab('features')}
                                    className={`px-6 py-2 rounded-full transition-all ${activeTab === 'features' ? 'bg-green-600/20 text-green-400' : 'text-gray-400 hover:text-white'}`}
                                >
                                    <i className="fas fa-star mr-2"></i> Features
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content - Show Chat Interface directly to authenticated users */}
                <div className="container mx-auto px-4 pb-16 relative z-10">
                    <div className="bg-gray-800/30 border border-gray-700/50 backdrop-blur-md rounded-2xl shadow-xl overflow-hidden h-[calc(100vh-220px)] min-h-[500px]">
                        {isAuthenticated ? (
                            // Show LLM interface for authenticated users
                            <LLMChatInterface user={user} />
                        ) : (
                            // For non-authenticated users, show login prompt
                            <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                                <div className="w-20 h-20 rounded-full bg-gray-700/50 flex items-center justify-center mb-6">
                                    <i className="fas fa-lock text-yellow-400 text-3xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-4 text-white">Login Required</h3>
                                <p className="text-gray-300 mb-8 max-w-lg">
                                    You need to be logged in to access Sequoia A.I. 
                                    This helps us ensure the sustainability of our service and protect your chat history.
                                </p>
                                <div className="flex space-x-4">
                                    <button 
                                        onClick={() => window.dispatchEvent(new CustomEvent('app:login'))}
                                        className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-lg transition-all duration-300 flex items-center"
                                    >
                                        <i className="fas fa-sign-in-alt mr-2"></i>
                                        Log In
                                    </button>
                                    <button 
                                        onClick={() => window.dispatchEvent(new CustomEvent('app:register'))}
                                        className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all duration-300 flex items-center"
                                    >
                                        <i className="fas fa-user-plus mr-2"></i>
                                        Create Account
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content blocks for each tab */}
                <div className="container mx-auto px-4 pb-16 relative z-10">
                    {activeTab === 'green-ai' && (
                        <div className="py-8">
                            <h2 className="text-3xl font-bold mb-4">Sequoia Green AI</h2>
                            <p className="text-gray-300 mb-6">
                                Our Green AI feature provides personalized insights and recommendations on how to reduce your carbon footprint and live a more sustainable life.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-800 p-6 rounded-lg shadow-md">
                                    <h3 className="text-xl font-semibold mb-3">Carbon Footprint Tracker</h3>
                                    <p className="text-gray-400 mb-4">
                                        Monitor your daily activities and track your carbon emissions with our easy-to-use tracker.
                                    </p>
                                    <a 
                                        href="#"
                                        className="text-green-400 hover:underline"
                                    >
                                        Learn more about tracking your carbon footprint
                                    </a>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-md">
                                    <h3 className="text-xl font-semibold mb-3">Sustainable Living Tips</h3>
                                    <p className="text-gray-400 mb-4">
                                        Get personalized tips and suggestions on how to live sustainably based on your lifestyle and preferences.
                                    </p>
                                    <a 
                                        href="#"
                                        className="text-green-400 hover:underline"
                                    >
                                        Discover tips for sustainable living
                                    </a>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-md">
                                    <h3 className="text-xl font-semibold mb-3">Green AI Insights</h3>
                                    <p className="text-gray-400 mb-4">
                                        Receive insights and analysis on your progress towards a more sustainable lifestyle.
                                    </p>
                                    <a 
                                        href="#"
                                        className="text-green-400 hover:underline"
                                    >
                                        View your Green AI insights
                                    </a>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-md">
                                    <h3 className="text-xl font-semibold mb-3">Community Challenges</h3>
                                    <p className="text-gray-400 mb-4">
                                        Participate in community challenges to reduce carbon emissions and earn rewards.
                                    </p>
                                    <a 
                                        href="#"
                                        className="text-green-400 hover:underline"
                                    >
                                        Join a community challenge
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                    {activeTab === 'about' && (
                        <div className="py-8">
                            <h2 className="text-3xl font-bold mb-4">About Sequoia A.I.</h2>
                            <p className="text-gray-300 mb-6">
                                Sequoia A.I. is an innovative platform that connects students with expert teachers and advanced AI tools to enhance the learning experience.
                            </p>
                            <p className="text-gray-300 mb-6">
                                Our mission is to make quality education accessible to everyone, everywhere, while promoting sustainability and environmental consciousness.
                            </p>
                            <p className="text-gray-300 mb-6">
                                With Sequoia A.I., you can explore a wide range of subjects, get personalized learning assistance, and develop skills for the future.
                            </p>
                            <a 
                                href="#"
                                className="text-green-400 hover:underline"
                            >
                                Learn more about our mission and values
                            </a>
                        </div>
                    )}
                    {activeTab === 'features' && (
                        <div className="py-8">
                            <h2 className="text-3xl font-bold mb-4">Features of Sequoia A.I.</h2>
                            <ul className="list-disc list-inside space-y-2">
                                <li className="text-gray-300">
                                    <i className="fas fa-check-circle text-green-400 mr-2"></i>
                                    Interactive Chat Interface: Engage in real-time conversations with our AI-powered chat interface.
                                </li>
                                <li className="text-gray-300">
                                    <i className="fas fa-check-circle text-green-400 mr-2"></i>
                                    Expert Teacher Agents: Get assistance from specialized teacher agents in various subjects.
                                </li>
                                <li className="text-gray-300">
                                    <i className="fas fa-check-circle text-green-400 mr-2"></i>
                                    Personalized Learning: Receive tailored learning experiences and resources based on your needs.
                                </li>
                                <li className="text-gray-300">
                                    <i className="fas fa-check-circle text-green-400 mr-2"></i>
                                    Progress Tracking: Monitor your learning progress and achievements over time.
                                </li>
                                <li className="text-gray-300">
                                    <i className="fas fa-check-circle text-green-400 mr-2"></i>
                                    Community Engagement: Connect with other learners and participate in community challenges and discussions.
                                </li>
                            </ul>
                            <a 
                                href="#"
                                className="text-green-400 hover:underline mt-4 inline-block"
                            >
                                Discover all features of Sequoia A.I.
                            </a>
                        </div>
                    )}
                </div>

                <style jsx>{`
                    @keyframes floatParticle {
                        0%, 100% {
                            transform: translateY(0) translateX(0);
                        }
                        25% {
                            transform: translateY(-20px) translateX(10px);
                        }
                        50% {
                            transform: translateY(-10px) translateX(20px);
                        }
                        75% {
                            transform: translateY(-30px) translateX(-10px);
                        }
                    }
                    .text-gradient {
                        background-clip: text;
                        -webkit-background-clip: text;
                        color: transparent;
                    }
                `}</style>
            </section>
        );
    } catch (error) {
        console.error('LLMPage error:', error);
        reportError(error);
        return null;
    }
}

window.LLMPage = LLMPage;
export default LLMPage;
